import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtDateShort, fmtRupiah, STATUS_LABELS, URGENCY_LABELS } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Car, Wrench, Wallet, ClipboardList, CalendarCheck, Download, FileText } from "lucide-react";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="rounded-md border border-slate-200 bg-white p-4 shadow-sm">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500">
      <Icon className="h-4 w-4 text-[var(--sipkd-primary)]" />
      {label}
    </div>
    <p className="mt-2 text-xl font-semibold text-slate-900">{value}</p>
  </div>
);

export default function Vehicles() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [plate, setPlate] = useState(params.get("plate") || "");
  const [vehicles, setVehicles] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/vehicles").then(({ data }) => setVehicles(data)).catch(() => {});
  }, []);

  const search = async (p) => {
    const query = (p ?? plate).trim();
    if (!query) return;
    setLoading(true);
    setParams({ plate: query.toUpperCase() });
    try {
      const res = await api.get(`/vehicles/${encodeURIComponent(query)}/history`);
      setData(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const p = params.get("plate");
    if (p) search(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const s = data?.summary;
  const v = data?.vehicle;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl md:text-5xl">
          Riwayat Perbaikan Kendaraan
        </h1>
        <p className="mt-2 text-base text-slate-500">
          Telusuri seluruh histori laporan, biaya, dan bengkel untuk satu nomor polisi.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 bg-white p-3 shadow-sm">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="plate-search"
            placeholder="Masukkan nomor polisi… (mis. B 1234 XYZ)"
            className="pl-9"
            value={plate}
            onChange={(e) => setPlate(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
          />
        </div>
        <Button
          data-testid="plate-search-btn"
          onClick={() => search()}
          className="rounded-full bg-[var(--sipkd-primary)] px-5 text-white transition-colors duration-300 hover:bg-[var(--sipkd-primary-hover)]"
        >
          Cari
        </Button>
        {data && (
          <>
            <Button
              data-testid="history-export-excel-btn"
              variant="outline"
              className="rounded-full"
              onClick={() =>
                window.open(`${API}/vehicles/${encodeURIComponent(data.plate_number)}/history/excel`, "_blank")
              }
            >
              <Download className="mr-1 h-4 w-4" /> Excel
            </Button>
            <Button
              data-testid="history-export-pdf-btn"
              variant="outline"
              className="rounded-full"
              onClick={() =>
                window.open(`${API}/vehicles/${encodeURIComponent(data.plate_number)}/history/pdf`, "_blank")
              }
            >
              <FileText className="mr-1 h-4 w-4" /> PDF
            </Button>
          </>
        )}
      </div>

      {vehicles.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {vehicles.map((veh) => (
            <button
              key={veh.id}
              data-testid={`plate-chip-${veh.plate_number}`}
              onClick={() => {
                setPlate(veh.plate_number);
                search(veh.plate_number);
              }}
              className={`rounded-md border px-3 py-1.5 text-sm font-num font-semibold shadow-sm transition-colors ${
                data?.plate_number === veh.plate_number
                  ? "border-[var(--sipkd-primary)] bg-[var(--sipkd-primary)]/5 text-[var(--sipkd-primary)]"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {veh.plate_number}
            </button>
          ))}
        </div>
      )}

      {loading && <p className="text-sm text-slate-400">Memuat riwayat…</p>}

      {data && !loading && (
        <div className="space-y-6" data-testid="vehicle-history-panel">
          {/* Identitas kendaraan */}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-md border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex items-center gap-2">
              <Car className="h-5 w-5 text-[var(--sipkd-primary)]" />
              <span data-testid="vehicle-plate" className="font-num text-xl font-bold text-slate-900">
                {data.plate_number}
              </span>
            </div>
            {v ? (
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
                <span>Merek: <b className="text-slate-900">{v.brand || "-"}</b></span>
                <span>Tipe: <b className="text-slate-900">{v.vehicle_type || "-"}</b></span>
                <span>Tahun: <b className="text-slate-900">{v.year || "-"}</b></span>
                {v.division_name && <span>Divisi: <b className="text-slate-900">{v.division_name}</b></span>}
              </div>
            ) : (
              <span className="text-sm text-slate-400">Kendaraan tidak terdaftar di master data</span>
            )}
          </div>

          {/* Ringkasan */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat testid="stat-total-reports" icon={ClipboardList} label="Total Laporan" value={s.total_reports} />
            <Stat testid="stat-total-cost" icon={Wallet} label="Total Biaya" value={fmtRupiah(s.total_cost)} />
            <Stat testid="stat-avg-cost" icon={Wrench} label="Rata-rata Biaya" value={fmtRupiah(Math.round(s.avg_cost))} />
            <Stat
              testid="stat-last-service"
              icon={CalendarCheck}
              label="Servis Terakhir"
              value={s.last_service ? fmtDateShort(s.last_service) : "-"}
            />
          </div>

          {/* Sebaran status & bengkel */}
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Sebaran Status</h3>
              {Object.keys(s.status_counts).length === 0 ? (
                <p className="text-sm text-slate-400">Belum ada data.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(s.status_counts).map(([st, c]) => (
                    <span key={st} className="inline-flex items-center gap-2 rounded-md bg-slate-50 px-3 py-1.5 text-xs text-slate-700">
                      {STATUS_LABELS[st] || st}
                      <b className="font-num text-slate-900">{c}</b>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Bengkel Terpakai</h3>
              {s.workshop_counts.length === 0 ? (
                <p className="text-sm text-slate-400">Belum ada bengkel tercatat.</p>
              ) : (
                <div className="space-y-2">
                  {s.workshop_counts.map((w) => (
                    <div key={w.name} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700">{w.name}</span>
                      <span className="font-num text-slate-500">{w.count}x</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Tabel riwayat */}
          <div className="sipkd-panel rounded-3xl p-3 sm:p-5">
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 px-5 py-4 text-sm font-semibold text-slate-900">
              Riwayat Laporan ({data.reports.length})
            </div>
            {data.reports.length === 0 ? (
              <p className="px-5 py-10 text-center text-sm text-slate-400">
                Tidak ada laporan untuk kendaraan ini.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-5 py-3">No. Laporan</th>
                      <th className="px-5 py-3">Tanggal</th>
                      <th className="px-5 py-3">Deskripsi</th>
                      <th className="px-5 py-3">Urgensi</th>
                      <th className="px-5 py-3">Bengkel</th>
                      <th className="px-5 py-3">Biaya</th>
                      <th className="px-5 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.reports.map((r) => (
                      <tr
                        key={r.id}
                        data-testid={`history-row-${r.report_number}`}
                        onClick={() => navigate(`/reports/${r.id}`)}
                        className="cursor-pointer hover:bg-slate-50"
                      >
                        <td className="whitespace-nowrap px-5 py-3 font-medium text-[var(--sipkd-primary)]">{r.report_number}</td>
                        <td className="whitespace-nowrap px-5 py-3 text-slate-500">{fmtDateShort(r.created_at)}</td>
                        <td className="max-w-[280px] truncate px-5 py-3 text-slate-600">{r.damage_description}</td>
                        <td className="whitespace-nowrap px-5 py-3 text-slate-600">{URGENCY_LABELS[r.urgency] || "-"}</td>
                        <td className="whitespace-nowrap px-5 py-3 text-slate-600">
                          {r.workshop_name || r.preferred_workshop_name || "-"}
                        </td>
                        <td className="whitespace-nowrap px-5 py-3 font-num text-slate-700">
                          {r.repair_cost ? fmtRupiah(r.repair_cost) : "-"}
                        </td>
                        <td className="px-5 py-3">
                          <StatusBadge status={r.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
